import { type RefObject, useRef, useEffect } from "react";
import { animate, createScope, type Scope } from "animejs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

type Props = {
    ref?: RefObject<HTMLElement | null>,
};

export default function OurTalentSection({}: Props) {
    const images = [
        {
            src: "https://oursharestudios.imgix.net/stock/portrait-man-01.jpg?q=80&fit=crop&w=990&h=1500",
            name: "John Doe",
        },
        {
            src: "https://oursharestudios.imgix.net/stock/portrait-man-02.jpg?q=80&fit=crop&w=990&h=1500",
            name: "David Smith",
        },
        {
            src: "https://oursharestudios.imgix.net/stock/portrait-man-03.jpg?q=80&fit=crop&w=990&h=1500",
            name: "Michael Johnson",
        },
        {
            src: "https://oursharestudios.imgix.net/stock/portrait-man-04.jpg?q=80&fit=crop&w=990&h=1500",
            name: "Chris Lee",
        },
        {
            src: "https://oursharestudios.imgix.net/stock/portrait-man-05.jpg?q=80&fit=crop&w=990&h=1500",
            name: "Robert Brown",
        },
        {
            src: "https://oursharestudios.imgix.net/stock/portrait-man-06.jpg?q=80&fit=crop&w=990&h=1500",
            name: "James Wilson",
        },
        {
            src: "https://oursharestudios.imgix.net/stock/portrait-woman-01.jpg?q=80&fit=crop&w=990&h=1500",
            name: "Emily Davis",
        },
        {
            src: "https://oursharestudios.imgix.net/stock/portrait-woman-02.jpg?q=80&fit=crop&w=990&h=1500",
            name: "Jessica Miller",
        },
    ];

    const talentBoardRootA = useRef<HTMLDivElement | null>(null);
    const talentBoardRootB = useRef<HTMLDivElement | null>(null);
    const talentBoardScopeA = useRef<Scope | null>(null);
    const talentBoardScopeB = useRef<Scope | null>(null);

    useEffect(() => {
        talentBoardScopeA.current = createScope({ root: talentBoardRootA }).add(
            () => {
                animate("#talentBoardGrid", {
                    loop: true,
                    translateX: [
                        {
                            from: "0%",
                            to: "-100%",
                            duration: 120000,
                            ease: "linear",
                        },
                    ],
                });
            },
        );

        talentBoardScopeB.current = createScope({ root: talentBoardRootB }).add(
            () => {
                animate("#talentBoardGrid", {
                    loop: true,
                    translateX: [
                        {
                            from: "100%",
                            to: "0%",
                            duration: 120000,
                            ease: "linear",
                        },
                    ],
                });
            },
        );

        return () => {
            talentBoardScopeA.current!.revert();
            talentBoardScopeB.current!.revert();
        };
    }, []);


    return <section className="relative px-4 space-y-8 min-h-[400px] md:min-h-[560px] mb-16 md:mb-64 mt-16 md:mt-32 overflow-x-clip overflow-y-visible">
        <div ref={talentBoardRootA} className="hidden md:block md:absolute py-8 w-full h-[125%] top-0 right-0 translate-x-1/3 mask-intersect mask-(--mask-fade-top-left) z-8">
            <div id="talentBoardGrid" className="grid grid-cols-4 grid-rows-2 w-full h-full z-5 gap-4">
                {images.map(({ src, name }, index) => <div key={`A + ${src} + ${index}`} className="relative p-2 rounded-lg shadow-lg z-5 bg-brand">
                    <div className="absolute bottom-8 invisible w-0 h-[40px] bg-brand flex items-center transition-all ease-in-out rounded-r">
                        <span className="pl-4 text-font font-medium uppercase min-w-max opacity-0 transition-all delay-50 ease-in-out">{name}</span>
                    </div>
                    <img src={src} className="w-full h-full object-cover object-center rounded-lg transition-all duration-300" />
                </div>)}
            </div>
        </div>

        <div ref={talentBoardRootB} className="hidden md:block md:absolute py-8 w-full h-[125%] top-0 -right-4 translate-x-1/3 mask-intersect mask-(--mask-fade-top-left) z-8">
            <div id="talentBoardGrid" className="grid grid-cols-4 grid-rows-2 w-full h-full z-5 gap-4">
                {images.map(({ src, name }, index) => <div key={`B + ${src} + ${index}`} className="relative p-2 rounded-lg shadow-lg z-5 bg-brand">
                    <div className="absolute bottom-8 invisible w-0 h-[40px] bg-brand flex items-center transition-all ease-in-out rounded-r">
                        <span className="pl-4 text-font font-medium uppercase min-w-max opacity-0 transition-all delay-50 ease-in-out">{name}</span>
                    </div>
                    <img src={src} className="w-full h-full object-cover object-center rounded-lg transition-all duration-300" />
                </div>)}
            </div>
        </div>

        <div className="p-4 md:p-8 h-full space-y-16 z-10">
            <h1 className="mt-16 relative text-3xl/[2.5rem] md:text-6xl/[4rem] uppercase font-bold tracking-normal border-b-4 pb-4 max-w-full md:min-w-1/3 md:max-w-1/2 mb-16 z-10">
                A Studio With Everyone In Mind
            </h1>
            <div className="inline-flex">
                <div className="relative flex flex-col justify-between gap-8 max-w-full md:max-w-1/2 space-y-16 tracking-wide h-content z-10 font-medium text-lg/[1.5rem] md:text-xl/[1.75rem] uppercase">
                    <p>
                        We foster exceptional results for your projects by
                        offering our talented artists and creatives a share
                        of the earnings.
                    </p>

                </div>
            </div>

            <ul className="pl-8 font-medium uppercase space-y-2 tracking-tight">
                <li className="flex justify-between items-center w-max gap-4">
                    <FontAwesomeIcon icon={faArrowRight} size="sm" className="text-brand" />
                    <span>We manage the logistics.</span>
                </li>
                <li className="flex justify-between items-center w-max gap-4">
                    <FontAwesomeIcon icon={faArrowRight} size="sm" className="text-brand" />
                    <span>Our talent delivers the magic.</span>
                </li>
                <li className="flex justify-between items-center w-max gap-4">
                    <FontAwesomeIcon icon={faArrowRight} size="sm" className="text-brand" />
                    <span>You get exceptional results.</span>
                </li>
            </ul>
        </div>
    </section>
}
